import { AxiosResponse } from 'axios';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import qs from 'query-string';
import axios from 'libs/Axios';

import { PaginationAndSortingParams } from 'types/api/Common/PaginationTypes';
import { AxiosDefaultErrorEntity } from 'types/api/Common/ErrorTypes';
import {
  CopyDataTenantItem,
  DeployTenantRequest,
  DeployTenantResponse,
  TenantDeploymentStatus,
  TenantDetails,
  TenantListResponse,
} from 'types/api/TenantMaster/MasterTable/TenantTableDataTypes';
import { DropdownItem } from 'types/api/SystemTenant/AROKMS/TableDefinitionTypes';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';

export const TENANT_DATA_QUERY = {
  TENANT_LIST: 'TENANT_LIST',
  TENANT_DROPDOWN: 'TENANT_DROPDOWN',
  TENANT_DETAILS: 'TENANT_DETAILS',
  LIST_ORGANIZATION_DROPDOWN: 'LIST_ORGANIZATION_DROPDOWN',
  COPY_DATA_TENANT_LIST: 'COPY_DATA_TENANT_LIST',
  COPY_DATA_SUBJECT_LIST: 'COPY_DATA_SUBJECT_LIST',
  DEPLOYMENT_STATUS: 'DEPLOYMENT_STATUS',
};

export function useTenantList(params: PaginationAndSortingParams) {
  return useQuery<AxiosResponse<TenantListResponse>, AxiosDefaultErrorEntity>(
    [TENANT_DATA_QUERY.TENANT_LIST, { ...params }],
    () =>
      axios.get(`/api/v1/master/tenant/list?${qs.stringify({ ...params, page: params.page - 1 })}`).then((res) => res)
  );
}

export function useTenantDetails(tenantId?: string) {
  return useQuery<AxiosResponse<TenantDetails>, AxiosDefaultErrorEntity>(
    [TENANT_DATA_QUERY.TENANT_DETAILS, { tenantId }],
    () => axios.get(`/api/v1/master/tenant/details/${tenantId}`).then((res) => res),
    {
      enabled: !!tenantId,
    }
  );
}

export function useGetListOrganizationDropdown(systemId?: string, includeCustomerOrganizations = false) {
  return useQuery<AxiosResponse<DropdownItem[]>, AxiosDefaultErrorEntity>(
    [TENANT_DATA_QUERY.LIST_ORGANIZATION_DROPDOWN, { systemId, includeCustomerOrganizations }],
    () =>
      axios
        .get(`/api/v1/master/tenant/dropdown/organization/${systemId}`, {
          params: { includeCustomerOrganizations },
        })
        .then((res) => res),
    {
      enabled: !!systemId,
    }
  );
}

export function useCopyDataTenantList(systemId?: string) {
  return useQuery<AxiosResponse<CopyDataTenantItem[]>, AxiosDefaultErrorEntity>(
    [TENANT_DATA_QUERY.COPY_DATA_TENANT_LIST, { systemId }],
    () => axios.get(`/api/v1/master/tenant/copy-data/${systemId}`).then((res) => res),
    {
      enabled: !!systemId,
    }
  );
}

export function useCopyDataSubjectList(copyTenantId?: string) {
  return useQuery<AxiosResponse<AutoCompleteItem[]>, AxiosDefaultErrorEntity>(
    [TENANT_DATA_QUERY.COPY_DATA_SUBJECT_LIST, { copyTenantId }],
    () => axios.get(`/api/v1/master/tenant/copy-data/subject/${copyTenantId}`).then((res) => res),
    {
      enabled: !!copyTenantId,
    }
  );
}

export function useDeployTenant() {
  const queryClient = useQueryClient();
  return useMutation<AxiosResponse<DeployTenantResponse>, AxiosDefaultErrorEntity, DeployTenantRequest>(
    (bodyRequest) => axios.post('/api/v1/master/tenant/create', bodyRequest),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(TENANT_DATA_QUERY.TENANT_LIST);
        queryClient.invalidateQueries(TENANT_DATA_QUERY.TENANT_DROPDOWN);
      },
    }
  );
}

export function useTenantDeploymentStatus(tenantId?: string | null) {
  return useQuery<AxiosResponse<TenantDeploymentStatus>, AxiosDefaultErrorEntity>(
    [TENANT_DATA_QUERY.DEPLOYMENT_STATUS, { tenantId }],
    () => axios.get(`/api/v1/master/tenant/deployment-status/${tenantId}`).then((res) => res),
    {
      enabled: !!tenantId,
      refetchInterval: (data) => {
        const status = data?.data?.deploymentStatus;
        if (status === 'COMPLETED' || status === 'FAILED') return false;
        return 3000;
      },
    }
  );
}

export function useUpdateTenant() {
  const queryClient = useQueryClient();
  return useMutation<AxiosResponse, AxiosDefaultErrorEntity, DeployTenantRequest>(
    (bodyRequest) => axios.put(`/api/v1/master/tenant/update/${bodyRequest.id}`, bodyRequest),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(TENANT_DATA_QUERY.TENANT_LIST);
        queryClient.invalidateQueries(TENANT_DATA_QUERY.TENANT_DROPDOWN);
      },
    }
  );
}
